import React, { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@ui/lib/utils';
import { PhillLogo } from './PhillLogo';

export type EnterFrom = 'left' | 'right';

/**
 * The side the panel should slide in from, carried on the navigation itself: a link deeper into the
 * flow sets `right`, a link back sets `left`. Undefined when the screen was opened directly, so a
 * cold load does not animate.
 */
export const useEnterFrom = (): EnterFrom | undefined =>
  (useLocation().state as { enterFrom?: EnterFrom } | null)?.enterFrom;

export interface AuthLayoutProps {
  children: ReactNode;
  // Which side the panel slides in from, matching the sign-in step change: `right` going deeper,
  // `left` coming back. Left out on a cold load, where there is no move to show.
  enterFrom?: EnterFrom;
  // Turns the logo into a way back. Screens with nothing behind them leave it out.
  backTo?: string;
}

/**
 * The signed-out shell: form on the left half, Panenco photo on the right. Sign-in, forgot password
 * and reset password all render through it, so moving between them only changes the panel content.
 */
export const AuthLayout = ({ children, enterFrom, backTo }: AuthLayoutProps): ReactElement => {
  const { t } = useTranslation();
  const logo = <PhillLogo className="h-11 w-11 text-foreground" />;

  return (
    <div className="flex min-h-screen">
      {/* Left — the form in a half-width column, so the form and the photo split the screen 50/50 on
          desktop. On mobile it takes the full width. The form itself is centered within the half via
          `items-center`. */}
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden bg-background px-6 py-8 md:w-1/2">
        {/* Pinned to the corner, matching the client top-bar's logo offset (16px in, ~10px down) so
            the logo stays put when you cross from the login view into the app. Independent of the
            form's padding. */}
        <div className="absolute left-4 top-2.5">
          {backTo ? (
            <Link to={backTo} state={{ enterFrom: 'left' }} aria-label={t('passwordReset.backToSignIn')}>
              {logo}
            </Link>
          ) : (
            logo
          )}
        </div>
        <div
          className={cn(
            'w-full max-w-sm',
            enterFrom && 'duration-300 ease-out animate-in fade-in-0',
            enterFrom === 'right' && 'slide-in-from-right-8',
            enterFrom === 'left' && 'slide-in-from-left-8',
          )}
        >
          {children}
        </div>
      </div>

      {/* Right — full-bleed Panenco photo (desktop only). `bg-zinc-900` is a brand-dark fallback so
          the panel still looks intentional if the image asset is missing. Drop the photo at
          apps/web/public/assets/images/login-hero.jpg. */}
      <div
        className="hidden bg-zinc-900 bg-cover bg-center md:block md:w-1/2"
        style={{ backgroundImage: 'url(/assets/images/login-hero.jpg)' }}
        aria-hidden
      />
    </div>
  );
};

export default AuthLayout;
